"use client";

import { useMemo, useState } from "react";
import { Frame, Slider, Verdict, Note } from "./parts";

const L = {
    fr: {
        title: "Ce qu'un petit taux de résiliation fait sur un an",
        churn: "Résiliations par mois",
        halfLife: "Moitié des clients partis après",
        afterYear: "Clients restants après 12 mois",
        replace: "Nouveaux clients à trouver chaque mois pour ne pas reculer",
        months: "mois",
        on100: "pour 100 clients",
        good: "À ce rythme, la croissance vient surtout des nouveaux clients gagnés.",
        warn: "Une part importante de votre acquisition sert déjà à remplacer les partants.",
        bad: "Vous remplacez plus que vous ne gagnez : l'acquisition ne fait que compenser.",
        note: "La composition est ce qui surprend : le taux ne s'additionne pas, il se compose. 5 % par mois ne font pas 60 % sur l'année mais environ 46 %.",
    },
    en: {
        title: "What a small cancellation rate does over a year",
        churn: "Cancellations per month",
        halfLife: "Half your customers gone after",
        afterYear: "Customers left after 12 months",
        replace: "New customers needed each month just to hold steady",
        months: "months",
        on100: "per 100 customers",
        good: "At this rate, growth comes mostly from customers genuinely gained.",
        warn: "A large share of your acquisition already goes to replacing leavers.",
        bad: "You are replacing more than you gain: acquisition only compensates.",
        note: "Compounding is the surprise: the rate does not add up, it compounds. Five percent a month is not 60 percent over the year but roughly 46.",
    },
};

const W = 560;
const H = 180;
const PAD = { l: 34, r: 12, t: 12, b: 26 };
const SPAN = 24;

export default function ChurnCompounding({ lang = "fr" }) {
    const t = L[lang] || L.fr;
    const [churn, setChurn] = useState(5);

    const r = useMemo(() => {
        const keep = 1 - churn / 100;
        const curve = Array.from({ length: SPAN + 1 }, (_, m) => 100 * keep ** m);
        // Demi-vie : m tel que keep^m = 0,5
        const halfLife = Math.log(0.5) / Math.log(keep);
        return { curve, halfLife, afterYear: curve[12] };
    }, [churn]);

    const x = (m) => PAD.l + (m / SPAN) * (W - PAD.l - PAD.r);
    const y = (v) => H - PAD.b - (v / 100) * (H - PAD.t - PAD.b);

    const path = r.curve.map((v, m) => `${m === 0 ? "M" : "L"}${x(m)} ${y(v)}`).join(" ");
    const tone = churn <= 3 ? "good" : churn <= 7 ? "warn" : "bad";

    return (
        <Frame icon="chart-square-linear" title={t.title}>
            <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img" aria-label={t.title}>
                <line x1={PAD.l} y1={H - PAD.b} x2={W - PAD.r} y2={H - PAD.b} stroke="currentColor" strokeOpacity="0.2" />
                <line x1={PAD.l} y1={PAD.t} x2={PAD.l} y2={H - PAD.b} stroke="currentColor" strokeOpacity="0.2" />

                {/* Repère de la moitié */}
                <line x1={PAD.l} y1={y(50)} x2={W - PAD.r} y2={y(50)} stroke="currentColor" strokeOpacity="0.25" strokeDasharray="4 4" />
                <text x={PAD.l - 5} y={y(50) + 3} textAnchor="end" fontSize="10" fill="currentColor" fillOpacity="0.6">50</text>
                <text x={PAD.l - 5} y={y(100) + 3} textAnchor="end" fontSize="10" fill="currentColor" fillOpacity="0.6">100</text>

                <path d={path} fill="none" stroke="#E8511D" strokeWidth="2.5" />

                {r.halfLife <= SPAN && (
                    <>
                        <line x1={x(r.halfLife)} y1={y(50)} x2={x(r.halfLife)} y2={H - PAD.b} stroke="#E8511D" strokeOpacity="0.45" strokeDasharray="3 3" />
                        <circle cx={x(r.halfLife)} cy={y(50)} r="4.5" fill="#E8511D" />
                        <text x={x(r.halfLife)} y={H - 8} textAnchor="middle" fontSize="10" fill="currentColor" fillOpacity="0.7">
                            {Math.round(r.halfLife)} {t.months}
                        </text>
                    </>
                )}
                <text x={W - PAD.r} y={H - 8} textAnchor="end" fontSize="10" fill="currentColor" fillOpacity="0.6">{SPAN} {t.months}</text>
            </svg>

            <div className="mt-4 max-w-sm">
                <Slider label={t.churn} value={churn} onChange={setChurn} min={1} max={20} step={1} display={`${churn} %`} />
            </div>

            <div className="grid sm:grid-cols-3 gap-3 mt-4">
                <div className="rounded-xl bg-primary/[0.06] dark:bg-primary/10 p-4">
                    <div className="text-sm text-gray-600 dark:text-lightBlue mb-0">{t.halfLife}</div>
                    <div className="text-2xl font-bold text-primary tabular-nums">
                        {Math.round(r.halfLife)} {t.months}
                    </div>
                </div>
                <div className="rounded-xl bg-gray-100/70 dark:bg-white/[0.04] p-4">
                    <div className="text-sm text-gray-600 dark:text-lightBlue mb-0">{t.afterYear}</div>
                    <div className="text-2xl font-bold tabular-nums dark:text-white">
                        {Math.round(r.afterYear)}
                        <span className="text-sm font-normal text-gray-500 dark:text-lightBlue/70"> / 100</span>
                    </div>
                </div>
                <div className="rounded-xl bg-gray-100/70 dark:bg-white/[0.04] p-4">
                    <div className="text-sm text-gray-600 dark:text-lightBlue mb-0">{t.replace}</div>
                    <div className="text-2xl font-bold tabular-nums dark:text-white">
                        {churn}
                        <span className="text-sm font-normal text-gray-500 dark:text-lightBlue/70"> {t.on100}</span>
                    </div>
                </div>
            </div>

            <Verdict tone={tone}>{t[tone]}</Verdict>
            <Note>{t.note}</Note>
        </Frame>
    );
}
